/* KOYO-CUSTOM: ada-cart-a11y — announce cart additions as a status message (WCAG 4.1.3).
 *
 * Xtra's ajax add-to-cart (ajaxCart.showCartPanel -> openPanel('cart')) DOES manage focus: openPanel
 * moves focus to the drawer's first control on open and returns it to the header cart link on close via
 * its .m6pn-clicked marker. What it does NOT do is announce the addition, so screen-reader users get no
 * confirmation. This adds the missing 4.1.3 status announcement.
 *
 * Correlate the announcement with the ACTUAL add-request completion, not with a #cart-count heuristic.
 * Xtra performs adds via `fetch('/cart/add.js', ...)` and processes them in handleAddToCartResponse; a
 * count-based guess cannot reliably tell an add from a quantity edit (/cart/change.js also bumps the
 * count) or from Xtra's synthetic in-cart change events, and a wall-clock token mishandles slow
 * responses, overlapping adds, and rejected adds. So we non-destructively wrap fetch (and XHR) and
 * announce only on a SUCCESSFUL /cart/add.js response:
 *   - a genuine add resolves 200 -> announced, whenever it arrives (no timing window);
 *   - a rejected/blocked add (e.g. a min-order app) is non-2xx or never fires -> not announced;
 *   - a quantity edit hits /cart/change.js -> not matched;
 *   - each concurrent add resolves independently -> each announced.
 *
 * Scope: announcement only. The 2026-08-18 VoiceOver run showed Xtra's own focus handling does not survive
 * the drawer re-render (focus stayed on Add to cart, F-2026-08-18-tc-05), so drawer focus, containment and
 * Escape now live in assets/koyo-a11y-dialog.js. A status message must not steal focus, which is why the
 * two concerns stay in separate files.
 */
(function () {
  'use strict';

  if (window.__koyoCartA11y) return; // idempotent: never wrap fetch/XHR twice
  window.__koyoCartA11y = true;

  var liveRegion = null;
  // Prefer the region rendered by layout/theme.liquid. VoiceOver did not announce the previous
  // create-on-demand region (SR run 2026-08-18, F-2026-08-18-tc-02): a region inserted in the same
  // task as its text is not yet being observed by the AT. Only fall back to creating one if the
  // layout node is missing (e.g. a section rendered outside the theme layout).
  function ensureLive() {
    if (liveRegion && liveRegion.isConnected) return liveRegion;
    liveRegion = document.getElementById('koyo-a11y-live');
    if (liveRegion) return liveRegion;
    liveRegion = document.createElement('div');
    liveRegion.id = 'koyo-a11y-live';
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.style.cssText = 'position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
    (document.body || document.documentElement).appendChild(liveRegion);
    return liveRegion;
  }
  // While the drawer is open it owns the announcement: it is an aria-modal dialog, so this region (a
  // sibling of #root) is in the pruned part of the page anyway, and the 532fea7 run showed the same
  // sentence landing in two polite regions at once. One live region at a time, and the one inside the
  // modal wins. Only announce from here when the drawer is not the surface being used.
  function drawerOwnsAnnouncement() {
    if (!window.__koyoA11yDialog) return false; // dialog module absent: this region is the only channel
    var drawer = document.getElementById('cart');
    return Boolean(drawer && drawer.getAttribute('aria-hidden') === 'false');
  }

  function cartCount() {
    var node = document.getElementById('cart-count');
    var count = node ? parseInt((node.textContent || '').trim(), 10) : NaN;
    return isNaN(count) ? null : count;
  }

  function submittedQuantity(init) {
    var body = init && init.body;
    var value = null;
    try {
      if (body && typeof body.get === 'function') value = body.get('quantity');
      else if (typeof body === 'string') value = new URLSearchParams(body).get('quantity');
      else if (body && typeof body === 'object' && body.quantity != null) value = body.quantity;
    } catch (e) { /* an opaque/custom request body falls back to the generic confirmation */ }
    var quantity = parseInt(value, 10);
    return isNaN(quantity) || quantity < 1 ? null : quantity;
  }

  function messageForCount(count) {
    return 'Added to your cart.' + (count == null ? '' : ' ' + count + (count === 1 ? ' item' : ' items') + ' in cart.');
  }

  function announce(msg) {
    try {
      document.dispatchEvent(new CustomEvent('koyo:cart-added', { detail: { message: msg } }));
    } catch (e) { /* CustomEvent unavailable — fall through to the layout region */ }
    if (drawerOwnsAnnouncement()) return;
    var el = ensureLive();
    el.textContent = '';
    setTimeout(function () { el.textContent = msg; }, 80);
  }

  function isAddUrl(u) { return /\/cart\/add(\.js)?(\?|#|$)/.test(String(u || '')); }

  // Announce after the add response, reading the resulting total once Xtra has updated #cart-count;
  // fall back to a generic confirmation if the count is not yet available.
  function announceAdd(beforeCount, quantity) {
    // Tell the dialog layer an add is resolving before the count is readable. The drawer opens now, and
    // it must have the confirmation in its heading before focus lands. Our observer is attached to the
    // fetch promise before Xtra attaches its response handler, so this event runs before Xtra opens the
    // drawer. Where the submitted quantity is readable, the first dialog-entry phrase gets the exact
    // resulting count; otherwise it still gets an unambiguous successful-add confirmation.
    try {
      document.dispatchEvent(new CustomEvent('koyo:cart-add-pending'));
      var expectedCount = beforeCount == null || quantity == null ? null : beforeCount + quantity;
      document.dispatchEvent(new CustomEvent('koyo:cart-add-ready', { detail: { message: messageForCount(expectedCount) } }));
    } catch (e) { /* no-op: the dialog layer then focuses on its own schedule */ }
    setTimeout(function () {
      announce(messageForCount(cartCount()));
    }, 250);
  }

  // Non-destructively wrap fetch: observe the /cart/add.js response, always returning Xtra's own promise.
  if (typeof window.fetch === 'function') {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && (input.url || input.href)) || ''; // string | Request(.url) | URL(.href)
      var beforeCount = isAddUrl(url) ? cartCount() : null;
      var quantity = isAddUrl(url) ? submittedQuantity(init) : null;
      var p = origFetch.apply(this, arguments);
      if (isAddUrl(url) && p && typeof p.then === 'function') {
        p.then(function (resp) { if (resp && resp.ok) announceAdd(beforeCount, quantity); }, function () {});
      }
      return p;
    };
  }

  // Belt-and-suspenders XHR wrap (Xtra uses fetch, but apps/other paths may use XHR).
  if (window.XMLHttpRequest && XMLHttpRequest.prototype) {
    var xopen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try { this.__koyoAdd = isAddUrl(url); } catch (e) { /* no-op */ }
      return xopen.apply(this, arguments);
    };
    var xsend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (this.__koyoAdd) {
        var beforeCount = cartCount();
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) announceAdd(beforeCount, null);
        }, { once: true }); // one-shot: a reused XHR instance won't retain a stale listener
      }
      return xsend.apply(this, arguments);
    };
  }

  // Escape / focus handling for the open drawer lives in assets/koyo-a11y-dialog.js, which owns the
  // drawer's modal semantics. This file stays announce-only.
})();
